// Copyright 2015-2023 Nstream, inc.
// All rights reserved.

import {Form} from "@swim/structure";
import type {Value} from "@swim/structure";
import {Recon} from "@swim/recon";
import {MapDownlink} from "@swim/client";
import type {Trait} from "@swim/model";
import {ViewRef} from "@swim/view";
import {ViewSet} from "@swim/view";
import {TraitViewRef} from "@swim/controller";
import type {PanelView} from "@swim/panel";
import type {ColLayout} from "@swim/table";
import type {ColView} from "@swim/table";
import type {HeaderView} from "@swim/table";
import type {TextCellView} from "@swim/table";
import type {TextColView} from "@swim/table";
import {TimeTableController} from "@nstream/widget";
import {MapDataController} from "./MapDataController";

/** @public */
export class MapLaneController extends TimeTableController {
  @TraitViewRef({
    extends: true,
    initView(panelView: PanelView): void {
      super.initView(panelView);
      panelView.set({
        headerTitle: "MapLane Data",
        headerSubtitle: "Lane",
      });
      this.owner.tablePanel.insertView().style.set({
        marginTop: 48,
      });
      this.owner.table.insertView();
      this.owner.header.insertView();
    },
  })
  override readonly panel!: TraitViewRef<this, Trait, PanelView> & TimeTableController["panel"];

  @ViewRef({
    extends: true,
    createView(): HeaderView {
      const headerView = super.createView();
      (this.owner.nameCol.insertView(headerView) as TextColView).set({
        label: "Key",
      });
      (this.owner.latestCol.insertView(headerView) as TextColView).set({
        label: "Value",
      });
      return headerView;
    },
  })
  override readonly header!: ViewRef<this, HeaderView> & TimeTableController["header"];

  @ViewSet({
    extends: true,
    getLayout(colView: ColView): ColLayout | null {
      let colLayout = super.getLayout(colView) as ColLayout | null;
      if (colLayout !== null) {
        colLayout = colLayout.withFlex(8, 0, 0);
      }
      return colLayout;
    },
  })
  override readonly cols!: ViewSet<this, ColView> & TimeTableController["cols"];

  protected createMapDataController(key: Value, value: Value): MapDataController {
    const mapDataController = new MapDataController();
    (mapDataController.nameCell.insertView() as TextCellView).set({
      content: Recon.toString(key),
    });
    (mapDataController.latestCell.insertView() as TextCellView).set({
      content: Recon.toString(value),
    });
    mapDataController.latestCell.insertView();
    return mapDataController;
  }

  protected updateMapDataController(mapDataController: MapDataController, value: Value): MapDataController {
    (mapDataController.latestCell.insertView() as TextCellView).set({
      content: Recon.toString(value),
    });
    return mapDataController;
  }

  @MapDownlink({
    consumed: true,
    keyForm: Form.forValue(),
    didUpdate(key: Value, value: Value): void {
      //console.log("MapLaneController.pulseDownlink.didSet:", value.toLike());
      //const displayValue = Attr.of("update", key).concat(value);
      let mapDataController = this.owner.getChild(Recon.toString(key), MapDataController);
      if (mapDataController === null) {
        mapDataController = this.owner.createMapDataController(key, value);
        this.owner.series.addController(mapDataController, void 0, Recon.toString(key));
      } else {
        (mapDataController.nameCell.getView() as TextCellView).set({
          content: Recon.toString(key),
        });
        (mapDataController.latestCell.getView() as TextCellView).set({
          content: Recon.toString(value),
        });
      }
    },
    didRemove(key: Value, value: Value): void {
      //const displayValue = Attr.of("remove", key).concat(value);
      const mapDataController = this.owner.getChild(Recon.toString(key), MapDataController);
      if (mapDataController !== null) {
        this.owner.series.removeController(mapDataController);
      }
    }
  })
  readonly mapLaneDataDownlink!: MapDownlink<this, Value, Value>;
}
